import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ExternalLink, Maximize, X as CloseIcon, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

// Image imports
import img26 from '@/assets/img/26.jpg';
import img27 from '@/assets/img/27.jpg';
import img28 from '@/assets/img/28.jpg';
import img29 from '@/assets/img/29.jpg';
import img30 from '@/assets/img/30.jpg';
import img31 from '@/assets/img/31.jpg';
import img32 from '@/assets/img/32.jpg';
import img33 from '@/assets/img/33.jpg';
import img34 from '@/assets/img/34.jpg';
import img35 from '@/assets/img/35.jpg';
import img36 from '@/assets/img/36.jpg';

const artworks = [
  // Added 'mood' for potential dynamic background or overlay tints
  { id: 'bw026', src: img26, title: 'Metallic Whispers', medium: 'Archival Pigment Print', dimensions: '24x36', year: 2023, alt: 'Abstract industrial textures in high contrast monochrome', mood: 'dark' },
  { id: 'bw027', src: img27, title: 'Solitude', medium: 'Silver Gelatin Print', dimensions: '16x20', year: 2022, alt: 'Lone figure in an expansive urban environment', mood: 'muted' },
  { id: 'bw028', src: img28, title: 'Fractured Light', medium: 'Platinum Palladium Print', dimensions: '30x30', year: 2023, alt: 'Geometric light patterns through architectural structures', mood: 'bright' },
  { id: 'bw029', src: img29, title: 'Transient', medium: 'Digital C-Print', dimensions: '20x30', year: 2024, alt: 'Multiple exposure portrait with movement blur', mood: 'ethereal' },
  { id: 'bw030', src: img30, title: 'Coastal Abstraction', medium: 'Archival Pigment Print', dimensions: '40x60', year: 2023, alt: 'Minimalist shoreline composition in black and white', mood: 'serene' },
  { id: 'bw031', src: img31, title: 'Mechanical Rhythm', medium: 'Silver Gelatin Print', dimensions: '20x24', year: 2022, alt: 'Industrial machinery details with repeating patterns', mood: 'dark' },
  { id: 'bw032', src: img32, title: 'Veiled Identity', medium: 'Charcoal on Paper (Photo Reproduction)', dimensions: '18x24', year: 2024, alt: 'Partial portrait with dramatic shadow play', mood: 'mysterious' },
  { id: 'bw033', src: img33, title: 'Urban Fossil', medium: 'Digital Photograph', dimensions: '30x45', year: 2023, alt: 'Decaying urban elements in stark monochrome', mood: 'gritty' },
  { id: 'bw034', src: img34, title: 'Architect\'s Shadow', medium: 'Archival Pigment Print', dimensions: '24x36', year: 2024, alt: 'Dramatic architectural shadows creating geometric patterns', mood: 'sharp' },
  { id: 'bw035', src: img35, title: 'Liquid Architecture', medium: 'Digital C-Print', dimensions: '20x30', year: 2023, alt: 'Building reflections in urban puddles', mood: 'reflective' },
  { id: 'bw036', src: img36, title: 'Silent Protest', medium: 'Silver Gelatin Print', dimensions: '16x20', year: 2024, alt: 'Powerful figurative study with social commentary', mood: 'impactful' }
];


const FilmstripItem = ({ artwork, index, isActive, onClick, onHover }) => {
  const shouldReduceMotion = useReducedMotion();
  return (
    <motion.div
      className={`bnw-filmstrip-item ${isActive ? 'active' : ''}`}
      onClick={() => onClick(artwork, index)}
      onMouseEnter={() => onHover(artwork)}
      onMouseLeave={() => onHover(null)} // Clear hover
      variants={{
        initial: { opacity: 0, x: -20 },
        animate: { opacity: 1, x: 0, transition: { duration: 0.3, delay: index * 0.05 } }
      }}
      whileHover={shouldReduceMotion ? {} : { scale: 1.05, transition: { type: 'spring', stiffness: 300 } }}
    >
      <motion.img 
        src={artwork.src} 
        alt={`Thumbnail of ${artwork.title}`} 
        className="bnw-filmstrip-thumbnail"
        layoutId={`bnw-thumbnail-${artwork.id}`} // For potential future use
      />
      <div className="bnw-filmstrip-item-info">
        <h4 className="bnw-filmstrip-item-title">{artwork.title}</h4>
        <p className="bnw-filmstrip-item-year">{artwork.year}</p>
      </div>
       {isActive && <motion.div className="bnw-filmstrip-active-indicator" layoutId="bnw-active-filmstrip-indicator" />}
    </motion.div>
  );
};

const BlackAndWhite = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredArtwork, setHoveredArtwork] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isDarkMode, setIsDarkMode] = useState(true);
  const filmstripRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();

  const currentArtwork = artworks[activeIndex];

  useEffect(() => {
    const prefersLightScheme = window.matchMedia('(prefers-color-scheme: light)');
    const applyTheme = (isLightPreferred) => {
      document.documentElement.classList.toggle('dark', isLightPreferred);
      setIsDarkMode(!isLightPreferred);
    };
    applyTheme(prefersLightScheme.matches);
    const handleChange = (e) => applyTheme(e.matches);
    prefersLightScheme.addEventListener('change', handleChange);
    return () => prefersLightScheme.removeEventListener('change', handleChange);
  }, []);
  
  const handleFilmstripClick = (artwork, index) => {
    setActiveIndex(index);
  };

  const handleFilmstripHover = (artwork) => {
    if (!shouldReduceMotion) { // Only set hover if not reducing motion, to prevent jumpiness
      setHoveredArtwork(artwork);
    }
  };

  const openLightbox = () => setLightboxOpen(true);
  const closeLightbox = () => setLightboxOpen(false);

  const navigateLightbox = (direction) => {
    let newIndex = (activeIndex + direction + artworks.length) % artworks.length;
    setActiveIndex(newIndex);
  };


  const focusDisplayArtwork = hoveredArtwork || currentArtwork; // Show hovered or active

  return (
    <>
      <script dangerouslySetInnerHTML={{ __html: `/* Theme script from previous examples */` }} />

      <motion.div 
        className="bnw-page-container pg-section" // Use theme bg
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <header className="bnw-main-header">
          <motion.h1 
            className="pg-title-main-text"
            initial={{opacity:0, y:-20}} animate={{opacity:1, y:0, transition:{delay:0.1, duration:0.4}}}
          >
            Shades of <span className="pg-title-accent-text bnw-title-accent">Silence</span>
          </motion.h1>
          <motion.p 
            className="pg-subtitle-text"
            initial={{opacity:0, y:-20}} animate={{opacity:1, y:0, transition:{delay:0.2, duration:0.4}}}
          >
            Exploring the evocative power of monochrome.
          </motion.p>
        </header>

        <div className="bnw-gallery-layout">
          <motion.div className="bnw-filmstrip-panel" ref={filmstripRef} initial="initial" animate="animate">
            {artworks.map((art, index) => (
              <FilmstripItem
                key={art.id}
                artwork={art}
                index={index}
                isActive={index === activeIndex}
                onClick={handleFilmstripClick}
                onHover={handleFilmstripHover}
              />
            ))}
          </motion.div>

          <motion.div className="bnw-focus-panel">
            <AnimatePresence mode="wait">
              <motion.div
                key={focusDisplayArtwork.id} // Key change triggers animation
                className="bnw-focus-image-wrapper"
                onClick={openLightbox}
                initial={{ opacity: 0, scale: shouldReduceMotion ? 1 : 0.95 }}
                animate={{ opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.42, 0, 0.58, 1] } }}
                exit={{ opacity: 0, scale: shouldReduceMotion ? 1 : 0.9, transition: { duration: 0.3 } }}
                whileHover={shouldReduceMotion ? {} : { scale: 1.02, transition: { type: 'spring', stiffness: 200, damping:15} }}
              >
                <motion.img
                  src={focusDisplayArtwork.src}
                  alt={focusDisplayArtwork.alt}
                  className="bnw-focus-image"
                  layoutId={`bnw-main-image-${focusDisplayArtwork.id}`}
                />
                <div className="bnw-focus-image-overlay">
                    <Maximize size={32} />
                </div>
              </motion.div>
            </AnimatePresence>
            
            <motion.div className="bnw-focus-details">
              <AnimatePresence mode="wait">
                <motion.h2
                  key={`title-${currentArtwork.id}`} // Ensure title animates with image
                  className="bnw-focus-title text-color-heading"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0, transition: { delay: 0.2, duration: 0.4 } }}
                  exit={{ opacity: 0, y: -20, transition: { duration: 0.2 } }}
                >
                  {currentArtwork.title}
                </motion.h2>
              </AnimatePresence>
              <AnimatePresence mode="wait">
                <motion.p
                  key={`medium-${currentArtwork.id}`}
                  className="bnw-focus-medium text-color-paragraph"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0, transition: { delay: 0.25, duration: 0.4 } }}
                  exit={{ opacity: 0, y: -20, transition: { duration: 0.2 } }}
                >
                  {currentArtwork.medium} • {currentArtwork.dimensions} inches • {currentArtwork.year}
                </motion.p>
              </AnimatePresence>
                <motion.button 
                  className="bnw-view-details-button button-primary focus-ring-accent"
                  onClick={openLightbox}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { delay: 0.35 } }}
                  whileHover={shouldReduceMotion ? {} : { scale: 1.05 }} whileTap={shouldReduceMotion ? {} : { scale: 0.95 }}
                >
                  View Full Details <ArrowRight size={18} className="ml-2"/>
                </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      <AnimatePresence>
        {lightboxOpen && currentArtwork && (
          <motion.div
            className="bnw-lightbox-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition:{delay: 0.2} }}
            onClick={closeLightbox}
          >
            <motion.div
              className="bnw-lightbox-content bg-card"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 50, scale: shouldReduceMotion ? 1 : 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1, transition: { type:'spring', stiffness:150, damping:20, delay:0.05 } }}
              exit={{ opacity: 0, y: shouldReduceMotion ? 0 : 30, scale: shouldReduceMotion ? 1 : 0.95, transition: { duration: 0.2 } }}
            >
              <motion.div className="bnw-lightbox-image-container">
                <AnimatePresence mode="sync"> {/* Use sync for smoother image transitions inside lightbox */}
                    <motion.img
                        key={currentArtwork.id} // Key change for image transition
                        layoutId={`bnw-main-image-${currentArtwork.id}`}
                        src={currentArtwork.src}
                        alt={currentArtwork.alt}
                        className="bnw-lightbox-image"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1, transition: { duration: 0.3, delay: 0.1 } }} // Delay to allow layout transition to settle
                        exit={{ opacity: 0, transition: {duration: 0.1} }}
                    />
                </AnimatePresence>
              </motion.div>
              <div className="bnw-lightbox-details-panel text-color-paragraph">
                 <motion.h2 className="bnw-lightbox-title text-color-heading"
                    initial={{opacity:0, y:10}} animate={{opacity:1, y:0, transition:{delay:0.2}}}
                 >{currentArtwork.title}</motion.h2>
                 <motion.p className="bnw-lightbox-meta"
                    initial={{opacity:0, y:10}} animate={{opacity:1, y:0, transition:{delay:0.25}}}
                 >
                    {currentArtwork.medium} <br/>
                    {currentArtwork.dimensions} inches • {currentArtwork.year}
                 </motion.p>
                 <motion.p className="bnw-lightbox-description"
                    initial={{opacity:0, y:10}} animate={{opacity:1, y:0, transition:{delay:0.3}}}
                 >
                    {currentArtwork.alt} {/* Using alt as description */}
                 </motion.p>
                 <motion.button
                  className="mt-auto button-primary w-full py-3 text-base font-semibold focus-ring-accent"
                  initial={{opacity:0, y:10}} animate={{opacity:1, y:0, transition:{delay:0.35}}}
                  whileHover={shouldReduceMotion ? {} : { scale: 1.05 }} whileTap={shouldReduceMotion ? {} : { scale: 0.95 }}
                >
                  Enquire
                  <ExternalLink size={18} className="ml-2" />
                </motion.button>
              </div>
               {/* Lightbox Navigation */}
              <button onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }} className="bnw-lightbox-nav prev">
                <ChevronLeft size={30} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }} className="bnw-lightbox-nav next">
                <ChevronRight size={30} />
              </button>
            </motion.div>
            <motion.button onClick={closeLightbox} className="bnw-lightbox-close-btn" aria-label="Close lightbox"
                initial={{opacity:0, scale:0.7}} animate={{opacity:1, scale:1, transition:{delay:0.3}}} exit={{opacity:0, scale:0.7}}>
              <CloseIcon size={24} />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BlackAndWhite;